import { useQuery } from '@tanstack/react-query'
import {
  type OrganisationDto,
  useGetOrganisationForUserHook,
  useGetParcelShopForUserHook,
  useGetUserHook,
  type ParcelShop as ServicePointDto,
  type UserDto,
} from '@dhl/mdp-api'
import { type UUID } from '~/common/model'
import { getSettledValue, maybe } from '~/common/util'
import { getOrganisationInfo } from '~/modules/users/hooks/util'
import { convertToUser, getOrganisation, type User } from '~/modules/users/model'

export const USER_DETAILS_QUERY_KEY = 'USER_DETAILS'

const enrichUser = async (
  userDto: UserDto,
  getParcelShopForUser: ReturnType<typeof useGetParcelShopForUserHook>,
  getOrganisationForUser: ReturnType<typeof useGetOrganisationForUserHook>
): Promise<User> => {
  const shouldGetParcelShop = !!userDto.parcelShopPsfKey
  const shouldGetOrganisation = !!userDto.organisationId

  const [servicePointResult, organisationResult, terminalResult] = await Promise.allSettled([
    maybe<ServicePointDto | undefined>(shouldGetParcelShop, () => getParcelShopForUser(userDto.id)),
    maybe<OrganisationDto | null>(shouldGetOrganisation, () => getOrganisationForUser(userDto.id)),
    // @todo use the generated hook once the spec exposes a GET for `users/{id}/terminal`
    maybe<OrganisationDto>(
      shouldGetOrganisation,
      () => getOrganisation(userDto.id, 'terminal') as Promise<OrganisationDto>
    ),
  ])

  const servicePointDto = getSettledValue(servicePointResult)
  const organisationDto = getSettledValue(organisationResult)
  const terminalDto = getSettledValue(terminalResult)

  const organisationInfo = getOrganisationInfo(servicePointDto, organisationDto ?? undefined, terminalDto)
  return convertToUser({
    ...userDto,
    ...organisationInfo,
  })
}

export const useUser = (userId: UUID) => {
  const getUser = useGetUserHook()
  const getParcelShopForUser = useGetParcelShopForUserHook()
  const getOrganisationForUser = useGetOrganisationForUserHook()

  // eslint-disable-next-line @tanstack/query/exhaustive-deps
  const { data, isLoading, isFetching, refetch, error } = useQuery({
    queryKey: [USER_DETAILS_QUERY_KEY, userId],
    queryFn: async () => {
      const user = await getUser(userId)
      return enrichUser(user, getParcelShopForUser, getOrganisationForUser)
    },
    enabled: !!userId,
  })

  return {
    error,
    user: data,
    isLoading,
    isFetching,
    refetch,
  }
}
